import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { customFetch } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { computeRewardMath, normalizeLoyaltyConfig, pointsForOrder, rewardProblem, horarioDosTurnos, type RewardUse, type Turno } from "@workspace/pricing";
import { diaDaSemanaNaLoja, type PublicProgram, type PublicReward } from "@/lib/loyalty";
import { getStoreTimezone } from "@/lib/store-timezone";
import type { Product } from "@workspace/api-client-react";
import {
  applyDiscount,
  computeCardSurcharge as computeCardSurchargeCore,
  computeCashChange,
  computeItemTotal,
  computeOptionsTotal,
  prepCountFor,
  computeOrderTotal,
  type CardSurchargeConfig,
} from "@workspace/pricing";
import { getActivePromotion, getActiveQuantityPromo, gruposComPromocao, quantityPromoDiscounts, type PromoChannel } from "@/lib/promotion";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import { useThermalPrinter } from "@/hooks/use-thermal-printer";
import { orderToReceipt, formatReceiptBytes } from "@/lib/receipt-formatter";
import { formatCurrency, linkDoWhatsApp, telefoneDoLink, orderDisplayCode } from "@/lib/utils";
import { abreWhatsApp } from "@/lib/abreWhatsApp";
import {
  useDeliveryFee,
  applyFreeDelivery,
  type DeliveryAddress,
} from "@/lib/use-delivery-fee";
import {
  type CartItem,
  type CustomerSelection,
  type PaymentPart,
  type SelectedOption,
  type ModoDoPedido,
  buildSimpleCartItem,
  buildFullOrderForReceipt,
  buildWhatsAppMessage,
  productHasOptions,
  genId,
} from "@/components/pdv/PDVShared";
import { EDITED_ORDER_MESSAGE_DEFAULT } from "@/lib/messages";
import { dataUnicaDosItens, itensDeEncomenda, modoDoCarrinho } from "@/lib/encomendaUnica";

const EMPTY_ADDRESS: DeliveryAddress = {
  street: "",
  number: "",
  neighborhood: "",
  complement: "",
  city: "",
  state: "",
  zipCode: "",
  lat: null,
  lng: null,
  formattedAddress: "",
};

// O que define o lugar da entrega, e por ele a taxa: complemento e referência não mudam o lugar.
const chaveDoLugar = (a: Partial<DeliveryAddress>) =>
  JSON.stringify([
    ...[a.street, a.number, a.neighborhood, a.city, a.state].map((v) => String(v ?? "").trim().toLowerCase().replace(/\s+/g, " ")),
    String(a.zipCode ?? "").replace(/\D/g, ""),
    a.lat == null ? null : Number(a.lat),
    a.lng == null ? null : Number(a.lng),
  ]);

export interface UsePdvCartArgs {
  store: any;
  products: Product[];
  slug: string;
  ordersEndpoint: string;
  source?: string;
  /** Buscar também pelo código de barras/código próprio. Só com a feature
   *  "barcode" (Ultra ou direito da loja) — 06/10/2026. */
  matchBarcode?: boolean;
  /** Lançar na comanda ou na mesa (página Comandas/Mesas): o número da conta identifica, e o «Cliente obrigatório» do
   *  PDV não vale (Palco 47, 31/10/2026: a comanda 13 do Renato não lançava). */
  naConta?: boolean;
}

export function usePdvCart({
  store,
  products,
  slug,
  ordersEndpoint,
  source = "pdv",
  matchBarcode = false,
  naConta = false,
}: UsePdvCartArgs) {
  const { toast } = useToast();
  const printer = useThermalPrinter();

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [editingItem, setEditingItem] = useState<CartItem | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  // Data única no carrinho (loja com orderSingleDate, 05/10/2026): uma data e um horário para o pedido inteiro.
  const [encData, setEncData] = useState<string | null>(null);
  const [encHora, setEncHora] = useState<string | null>(null);
  // Turno da data única (12/10/2026): quando o produto mais detalhado do carrinho pede turno.
  const [encTurno, setEncTurno] = useState<Turno | null>(null);
  const [orderNotes, setOrderNotes] = useState("");
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState<"fixed" | "percent">("fixed");
  const [customer, setCustomer] = useState<CustomerSelection>({ kind: "avulso" });
  // Programa de pontos: prêmios aplicados no balcão (só no PDV do painel — o público não tem sessão de lojista).
  const [premiosPdv, setPremiosPdv] = useState<Array<{ rewardId: string; points?: number }>>([]);
  // De qual cliente são os prêmios escolhidos: trocou o cliente, eles saem.
  const premiosDoClienteRef = useRef<string | null>(null);
  const [deliveryMode, setDeliveryMode] = useState<ModoDoPedido>("retirada");
  const [address, setAddress] = useState<DeliveryAddress>(EMPTY_ADDRESS);
  const [paymentMixed, setPaymentMixed] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [cashReceived, setCashReceived] = useState(0);
  const [paymentParts, setPaymentParts] = useState<PaymentPart[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{
    orderId: string;
    orderNumber?: string;
    message: string;
    title?: string;
    printablePayload: any;
  } | null>(null);
  const [showCartMobile, setShowCartMobile] = useState(false);
  // Mesa/comanda: rótulo + "pagar ao fechar" (pagamento Pendente até a conta fechar).
  const [tableNumber, setTableNumber] = useState("");
  const [payLater, setPayLater] = useState(false);
  // Taxa de serviço do pedido aberto para editar (o «Fechar conta» cobrou): o servidor reaplica a porcentagem sobre os
  // produtos de agora, e o carrinho mostra a mesma conta. Pedido novo nunca tem.
  const [taxaServicoPct, setTaxaServicoPct] = useState(0);
  // Crediário: nº de parcelas e 1ª data (default +30 dias). O nº vem da
  // CONFIG do método ao selecioná-lo (o lojista pode ajustar caso a caso).
  const [crediarioInstallments, setCrediarioInstallments] = useState(2);
  const [crediarioFirstDue, setCrediarioFirstDue] = useState(() =>
    new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  );
  // Endereço salvo do cliente escolhido pra reuso (PDV interno). Editar o
  // endereço manualmente depois de escolher volta a tratar como endereço novo.
  const [savedAddressId, setSavedAddressId] = useState<string | null>(null);
  // O lojista mexeu no endereço (digitou, escolheu na busca, mudou o CEP ou escolheu um salvo). Na edição vai como
  // addressChanged: sem isso o servidor mantém o endereço do pedido.
  const enderecoMexidoRef = useRef(false);
  // Pedido sendo editado (mesa/correção). null = criando pedido novo.
  const [editingOrder, setEditingOrder] = useState<{
    id: string;
    dailyOrderNumber: number | null;
    wasPending: boolean;
    // "link" = pedido que o cliente fez no link público; edição libera o
    // aviso ao cliente e desliga o mínimo próprio do PDV.
    source: "pdv" | "link";
    loyaltyPointsUsed: number;
    // Frete a calcular no pedido carregado: a edição com taxa 0 mantém o pendente (o servidor só fecha com taxa > 0),
    // e o cupom/comprovante desta venda dizem «a calcular».
    deliveryFeePending: boolean;
    // Dias com encomenda no pedido carregado: não contam contra o limite de encomendas por dia (são deste pedido).
    diasAgendados: string[];
    // Cliente gravado no pedido, e se a tela o abriu como «Cliente avulso» (o balcão, ou pedido sem cliente): a edição
    // só manda o cliente quando o lojista troca.
    customerId: string | null;
    abriuAvulso: boolean;
    // Crediário com parcelas já lançadas no Financeiro: o nº e a 1ª data são os do pedido e não mudam na edição (o
    // servidor reparte a diferença do total nas que faltam receber).
    crediarioTravado: { parcelas: number; primeira: string } | null;
    // Pedido do link «A combinar» (loja sem entrega e sem retirada): a edição mostra a opção e, sem troca, a mantém.
    eraCombinar: boolean;
  } | null>(null);
  // Canal da promoção: o do pedido que está sendo editado (pedido do link segue a promoção do link), senão o PDV.
  const canalDaPromo: PromoChannel = editingOrder?.source === "link" ? "link" : "pdv";
  // Aviso ao cliente depois de editar pedido do link: texto pronto + telefone.
  // O lojista decide se envia (mesmo padrão das mensagens de status).
  const [notifyPrompt, setNotifyPrompt] = useState<{ message: string; phone: string } | null>(null);

  // Crediário: métodos com nome livre da loja entram junto das formas — os
  // botões e os selects do Misto herdam sem wiring extra.
  const crediarioCfgs = (((store as any)?.crediarioMethods ?? []) as Array<{ name: string; installments?: number; interestMode?: string; interestPercent?: number; interestPerInstallment?: number }>)
    .filter((m) => m?.name);
  const crediarioNames: string[] = crediarioCfgs.map((m) => m.name);
  const acceptedPayments: string[] = [
    ...(((store as any)?.acceptedPayments || ["Dinheiro", "PIX"]) as string[]),
    ...crediarioNames,
  ];
  const allowsDelivery = (store as any)?.allowsDelivery !== false;
  const pdvSettings = (store as any)?.pdvSettings || {};
  const dataUnica = (store as any)?.orderSingleDate === true;
  const horariosDosTurnos = useMemo(() => horarioDosTurnos((store as any)?.orderShiftHours, (store as any)?.businessHours), [store]);
  const modoEnc = modoDoCarrinho(itensDeEncomenda(cart));
  const encomendaCompleta = !!encData && (modoEnc === "horario" ? !!encHora : modoEnc === "turno" ? !!encTurno : true);
  const customerRequired = !naConta && !!pdvSettings.customerRequired;
  const useCustomDelivery = !!pdvSettings.useCustomDelivery;
  const customDeliveryFee = parseFloat(pdvSettings.customDeliveryFee || "0") || 0;
  const customMinOrderValue = parseFloat(pdvSettings.customMinOrderValue || "0") || 0;
  const customFreeDeliveryAbove = pdvSettings.customFreeDeliveryAbove
    ? parseFloat(pdvSettings.customFreeDeliveryAbove) || 0
    : 0;
  // Pedido mínimo da loja vale pro LINK público (economia da entrega), não pro
  // balcão — o PDV só exige mínimo quando setado nos valores próprios do PDV
  // (mesma isenção que o servidor aplica em orders.ts via !isPdv).
  // Editando pedido do LINK, o mínimo do PDV não vale (o servidor também isenta).
  const minOrderValue = useCustomDelivery && editingOrder?.source !== "link" ? customMinOrderValue : 0;

  // Override manual da taxa de entrega para o pedido em curso (operador
  // ajusta no balcao quando precisa cobrar diferente do configurado).
  const [feeOverride, setFeeOverride] = useState<number | null>(null);
  // Taxa gravada do pedido aberto para edição (Pedro, 18/10/2026): vale enquanto o lugar da entrega for o do pedido
  // (aqui fica a chave dele). O lojista mudou o endereço, vale a taxa do endereço novo; o ajuste à mão continua depois.
  const [taxaDoPedido, setTaxaDoPedido] = useState<string | null>(null);

  useEffect(() => {
    if (!paymentMethod && acceptedPayments.length > 0) {
      setPaymentMethod(acceptedPayments[0]);
    }
  }, [acceptedPayments, paymentMethod]);

  // "Pagar ao fechar" só existe com mesa — limpar a mesa desliga o modo.
  useEffect(() => {
    if (payLater && !tableNumber.trim()) setPayLater(false);
  }, [payLater, tableNumber]);

  // Selecionou um crediário → parcelas pré-preenchidas da config do método.
  const selectedCrediarioName = paymentMixed
    ? paymentParts.find((p) => crediarioNames.includes(p.method))?.method ?? null
    : crediarioNames.includes(paymentMethod) ? paymentMethod : null;
  const selectedCrediarioCfg = selectedCrediarioName
    ? crediarioCfgs.find((m) => m.name === selectedCrediarioName) ?? null
    : null;
  const parcelasTravadas = editingOrder?.crediarioTravado ?? null;
  const parcelas = parcelasTravadas?.parcelas ?? crediarioInstallments;
  const primeiraParcela = parcelasTravadas?.primeira ?? crediarioFirstDue;
  // Juros efetivos: modo "parcela" escala com o nº escolhido (1x sem juros).
  const crediarioInterest = !selectedCrediarioCfg
    ? 0
    : selectedCrediarioCfg.interestMode === "parcela"
      ? Math.min(Math.max(Number(selectedCrediarioCfg.interestPerInstallment) || 0, 0), 100) * Math.max(parcelas - 1, 0)
      : Math.min(Math.max(Number(selectedCrediarioCfg.interestPercent) || 0, 0), 100);
  // O pedido aberto para edição traz o nº de parcelas dele: a forma que entra junto não troca pelo padrão dela.
  const formaDoPedidoRef = useRef<string | null>(null);
  useEffect(() => {
    const doPedido = formaDoPedidoRef.current;
    formaDoPedidoRef.current = null;
    if (selectedCrediarioName && selectedCrediarioName === doPedido) return;
    if (selectedCrediarioCfg) {
      setCrediarioInstallments(Math.min(Math.max(Math.trunc(selectedCrediarioCfg.installments ?? 2), 1), 24));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrediarioName]);

  // «Esconder produtos indisponíveis no PDV» (Configurações → PDV; Pedro, 30/10/2026): desligado, o indisponível no link
  // aparece aqui para vender no balcão.
  const esconderIndisponiveis = (store as any)?.pdvSettings?.esconderIndisponiveis === true;
  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    // Código do produto também acha (23/09/2026); espaço não faz parte de código.
    const termCodigo = term.replace(/\s+/g, "");
    return (products as Product[]).filter((p: any) => {
      if (p.status === "inativo") return false;
      if (esconderIndisponiveis && p.status === "indisponivel") return false;
      if (activeCategory && p.categoryId !== activeCategory) return false;
      if (term) {
        const porNome = !!p.name?.toLowerCase().includes(term);
        const porCodigo = matchBarcode && !!termCodigo && String(p.barcode ?? "").toLowerCase().includes(termCodigo);
        if (!porNome && !porCodigo) return false;
      }
      return true;
    });
  }, [products, search, activeCategory, matchBarcode, esconderIndisponiveis]);

  const subtotal = useMemo(() => {
    const gross = cart.reduce(
      (sum, it) =>
        sum + computeItemTotal({ unitPrice: it.unitPrice, optionsTotal: it.optionsTotal, quantity: it.quantity }),
      0,
    );
    // Promo por quantidade: linhas a preço cheio + desconto no total, com a
    // MESMA fórmula do servidor (que divide as linhas em tiers no insert).
    const qtyDiscount = quantityPromoDiscounts(cart, "pdv")
      .reduce((s, e) => s + e.discount, 0);
    return Math.max(0, Math.round((gross - qtyDiscount) * 100) / 100);
  }, [cart]);

  const discountAmount = useMemo(
    () => applyDiscount(subtotal, discountValue, discountType),
    [subtotal, discountValue, discountType],
  );

  const baseAfterDiscount = useMemo(
    () => Math.max(0, subtotal - discountAmount),
    [subtotal, discountAmount],
  );

  // Estabilizar a referência: arrow inline aqui torna o setAddress recebido pelo
  // useDeliveryFee diferente a cada render, disparando seu useEffect interno em
  // loop (Maximum update depth / React error #185) quando feeType==="neighborhood".
  const setAddressUpdater = useCallback(
    (updater: (prev: DeliveryAddress) => DeliveryAddress) => setAddress(updater),
    [],
  );

  const rawFeeInfo = useDeliveryFee({
    slug,
    store,
    address,
    setAddress: setAddressUpdater,
    // «A combinar» não tem taxa nem endereço: para o frete, conta como não-entrega.
    deliveryMode: deliveryMode === "entrega" ? "entrega" : "retirada",
    enabled: !!slug && !!store && !useCustomDelivery,
  });

  // Quando o lojista define uma taxa especifica para o PDV, ignoramos o
  // resultado do useDeliveryFee (que olha bairros/distancia configurados pra
  // o link publico) e usamos o valor fixo definido nas configuracoes do PDV.
  // Tambem destravamos os bloqueios "feeNotResolved" / "noDeliveryBlocked"
  // que existem pra modos dinamicos do link publico.
  const feeInfo = useMemo(() => {
    // Override manual do operador tem prioridade absoluta sobre tudo.
    if (feeOverride !== null) {
      // A taxa gravada do pedido fixa só o valor: na taxa por bairro e por distância o endereço continua no seletor da
      // loja, para a troca recalcular. O ajuste à mão vira taxa fixa.
      const seletorDaLoja =
        taxaDoPedido !== null && !useCustomDelivery && (rawFeeInfo.feeType === "neighborhood" || rawFeeInfo.feeType === "distance");
      return {
        ...rawFeeInfo,
        feeType: seletorDaLoja ? rawFeeInfo.feeType : ("fixed" as const),
        deliveryFee: deliveryMode === "entrega" ? feeOverride : 0,
        feeLoading: false,
        noDeliveryBlocked: false,
        feeNotResolved: false,
        feePending: false,
        dynamicFee: null,
      };
    }
    if (!useCustomDelivery) return rawFeeInfo;
    return {
      ...rawFeeInfo,
      feeType: "fixed" as const,
      deliveryFee: deliveryMode === "entrega" ? customDeliveryFee : 0,
      feeLoading: false,
      noDeliveryBlocked: false,
      feeNotResolved: false,
      feePending: false,
      dynamicFee: null,
    };
  }, [rawFeeInfo, useCustomDelivery, customDeliveryFee, deliveryMode, feeOverride, taxaDoPedido]);

  const { fee: deliveryFee, freeApplied: freeDeliveryApplied } = useMemo(() => {
    // Override manual: respeita o valor digitado pelo operador (sem frete gratis).
    if (feeOverride !== null) {
      return { fee: feeInfo.deliveryFee, freeApplied: false };
    }
    // PDV em modo custom: aplica o "gratis acima de" especifico do PDV (se houver),
    // ignorando o do link publico.
    if (useCustomDelivery) {
      if (
        deliveryMode === "entrega" &&
        customFreeDeliveryAbove > 0 &&
        baseAfterDiscount >= customFreeDeliveryAbove &&
        feeInfo.deliveryFee > 0
      ) {
        return { fee: 0, freeApplied: true };
      }
      return { fee: feeInfo.deliveryFee, freeApplied: false };
    }
    return applyFreeDelivery(feeInfo.deliveryFee, baseAfterDiscount, store);
  }, [feeInfo.deliveryFee, baseAfterDiscount, store, useCustomDelivery, customFreeDeliveryAbove, deliveryMode, feeOverride]);

  const pontosNoPdv = ordersEndpoint === "/api/orders" && (store as any)?.loyaltyEnabled === true;
  const clientePontosId = customer.kind === "existing" ? (customer.customer as any)?.id ?? null : null;
  const { data: pontosCliente } = useQuery<{ enabled: boolean; balance: number; program: PublicProgram }>({
    queryKey: ["loyalty-customer", clientePontosId],
    queryFn: () => customFetch(`/api/loyalty/customers/${clientePontosId}`),
    enabled: pontosNoPdv && !!clientePontosId,
    staleTime: 15_000,
  });
  // Trocou de cliente: os prêmios escolhidos eram do outro (o pedido aberto para edição carrega os dele).
  useEffect(() => {
    if (clientePontosId !== premiosDoClienteRef.current) {
      premiosDoClienteRef.current = clientePontosId;
      setPremiosPdv([]);
    }
  }, [clientePontosId]);
  const escolherPremiosPdv = (next: Array<{ rewardId: string; points?: number }>) => {
    premiosDoClienteRef.current = clientePontosId;
    setPremiosPdv(next);
  };
  const programaPdv = pontosNoPdv && pontosCliente?.enabled ? pontosCliente.program : undefined;
  // Na edição, os pontos que o pedido já usa voltam antes da nova troca — só para o cliente do pedido: trocou o
  // cliente, o novo não fica com os pontos que o outro usou.
  const pontosDoPedido = editingOrder && clientePontosId && clientePontosId === editingOrder.customerId ? editingOrder.loyaltyPointsUsed : 0;
  const saldoPdv = (pontosCliente?.balance ?? 0) + pontosDoPedido;
  const problemaPdv = (r: PublicReward, saldoLivre = saldoPdv) =>
    rewardProblem({
      reward: { ...r, stock: null, stockSetAt: null, active: true },
      balance: saldoLivre,
      deliveryMode,
      netDelivery: deliveryFee,
      stockLeft: r.stockLeft,
    });
  const { rewardUsesPdv, rewardMathPdv } = useMemo(() => {
    const uses: RewardUse[] = [];
    if (programaPdv) {
      let livre = saldoPdv;
      for (const e of premiosPdv) {
        const r = programaPdv.rewards.find((x) => x.id === e.rewardId);
        if (!r || problemaPdv(r, livre)) continue;
        const points = r.type === "cashback" ? Math.max(r.cost, Math.min(livre, e.points ?? livre)) : r.cost;
        livre -= points;
        uses.push({ rewardId: r.id, name: r.name, type: r.type, value: r.value, points });
      }
    }
    const math = computeRewardMath({ netProducts: baseAfterDiscount, netDelivery: deliveryFee, uses });
    const comDesconto = uses
      .map((u, i) => ({ ...u, points: math.pointsPerUse[i] ?? u.points, discount: math.perUse[i] ?? 0 }))
      .filter((u) => u.points > 0);
    return { rewardUsesPdv: comDesconto, rewardMathPdv: math };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programaPdv, premiosPdv, saldoPdv, baseAfterDiscount, deliveryFee, deliveryMode]);
  const rendimentoPdv = programaPdv
    ? pointsForOrder(normalizeLoyaltyConfig(programaPdv), { base: rewardMathPdv.netProducts, weekday: diaDaSemanaNaLoja(getStoreTimezone()) })
    : 0;

  const surcharge = useMemo(() => {
    // Conta aberta (pagar ao fechar): sem forma definida, sem acréscimo.
    if (payLater) return 0;
    const cfgMap = ((store as any)?.cardSurchargeConfig ?? null) as
      | Record<string, CardSurchargeConfig | undefined>
      | null;
    if (paymentMixed) {
      const partsSum = paymentParts.reduce((s, p) => s + p.amount, 0) || 1;
      return paymentParts.reduce((sum, p) => {
        const partFee = (p.amount / partsSum) * rewardMathPdv.netDelivery;
        return sum + computeCardSurchargeCore(cfgMap, p.method, p.amount, partFee);
      }, 0);
    }
    return computeCardSurchargeCore(cfgMap, paymentMethod, rewardMathPdv.netProducts, rewardMathPdv.netDelivery);
  }, [payLater, paymentMixed, paymentMethod, paymentParts, store, rewardMathPdv.netProducts, rewardMathPdv.netDelivery]);

  const taxaServico =
    taxaServicoPct > 0 && deliveryMode !== "entrega" ? Math.round(rewardMathPdv.netProducts * taxaServicoPct) / 100 : 0;
  const total = useMemo(
    () =>
      Math.round(
        (computeOrderTotal({
          subtotal,
          discount: discountAmount + rewardMathPdv.loyaltyDiscount,
          deliveryFee,
          surcharge,
        }) +
          taxaServico) *
          100,
      ) / 100,
    [subtotal, discountAmount, surcharge, deliveryFee, rewardMathPdv.loyaltyDiscount, taxaServico],
  );

  const cashChange = useMemo(() => {
    if (payLater) return 0;
    if (paymentMixed) {
      const cashPart = paymentParts.find((p) => p.method === "Dinheiro");
      if (cashPart) return computeCashChange(cashReceived, cashPart.amount);
      return 0;
    }
    if (paymentMethod === "Dinheiro") return computeCashChange(cashReceived, total);
    return 0;
  }, [payLater, paymentMixed, paymentMethod, paymentParts, cashReceived, total]);

  const paymentPartsTotal = useMemo(
    () => paymentParts.reduce((s, p) => s + p.amount, 0),
    [paymentParts],
  );

  const handleAddOrUpdateItem = (
    qty: number,
    notes: string,
    selectedOptions: SelectedOption[],
    removedIngredients: string[],
    scheduledDate?: string,
    scheduledTime?: string,
    weightKg?: number,
    scheduledShift?: string,
  ) => {
    if (!detailProduct) return;
    const promo = getActivePromotion(detailProduct, new Date(), "pdv");
    const soldByWeight = !!(detailProduct as any).soldByWeight;
    const perUnitOrKg = promo.active ? promo.finalPrice : promo.originalPrice;
    // Por peso: o unitário da linha é peso × R$/kg (o servidor recalcula).
    const unitPrice = soldByWeight
      ? Math.round(perUnitOrKg * (weightKg ?? 0) * 100) / 100
      : perUnitOrKg;
    // Pacote de escolhas: os extras entram no valor das opções da unidade (a configuração só vem com o recurso).
    const prepDoItem = prepCountFor((detailProduct as any).optionGroups, (detailProduct as any).prepCount, selectedOptions);
    // Promoção em % no produto de preço base 0: as opções que formam o preço com o desconto, como o servidor cobra.
    const optionsTotal = computeOptionsTotal(gruposComPromocao(detailProduct, canalDaPromo), selectedOptions) + (prepDoItem?.extrasValue ?? 0);

    if (editingItem) {
      setCart((prev) =>
        prev.map((it) =>
          it.cartItemId === editingItem.cartItemId
            ? {
                ...it,
                quantity: qty,
                notes,
                selectedOptions,
                removedIngredients,
                optionsTotal,
                scheduledDate,
                scheduledTime,
                scheduledShift,
                ...(soldByWeight ? { weightKg, unitPrice } : {}),
              }
            : it,
        ),
      );
    } else {
      const item: CartItem = {
        cartItemId: genId(),
        product: detailProduct,
        quantity: qty,
        notes,
        selectedOptions,
        removedIngredients,
        optionsTotal,
        unitPrice,
        originalUnitPrice: promo.originalPrice,
        scheduledDate,
        scheduledTime,
        scheduledShift,
        weightKg: soldByWeight ? weightKg : undefined,
      };
      setCart((prev) => [...prev, item]);
    }
    setDetailProduct(null);
    setEditingItem(null);
  };

  const handleRemoveItem = (cartItemId: string) => {
    setCart((prev) => prev.filter((it) => it.cartItemId !== cartItemId));
  };

  const handleQtyChange = (cartItemId: string, qty: number) => {
    if (qty <= 0) {
      handleRemoveItem(cartItemId);
      return;
    }
    setCart((prev) =>
      prev.map((it) => (it.cartItemId === cartItemId ? { ...it, quantity: qty } : it)),
    );
  };

  const handleClearCart = () => {
    setCart([]);
    setEncData(null);
    setEncHora(null);
    setEncTurno(null);
    setOrderNotes("");
    setDiscountValue(0);
    setDiscountType("fixed");
    setCashReceived(0);
    setPaymentParts([]);
    setPaymentMixed(false);
    setCustomer({ kind: "avulso" });
    setDeliveryMode("retirada");
    setAddress(EMPTY_ADDRESS);
    // A lista de bairros mostra o bairro do endereço: zerou o endereço, zera a escolha. Sem isso, a entrega seguinte
    // mostrava o bairro anterior com o endereço sem bairro e travava em «Endereço incompleto» (Pedro, 22/10/2026).
    rawFeeInfo.setSelectedNeighborhood("");
    setFeeOverride(null);
    setTaxaDoPedido(null);
    setTableNumber("");
    setPayLater(false);
    setTaxaServicoPct(0);
    setSavedAddressId(null);
    enderecoMexidoRef.current = false;
    formaDoPedidoRef.current = null;
    setEditingOrder(null);
    setPremiosPdv([]);
  };

  // Endereço que vem pronto (o do pedido aberto, ou um salvo do cliente) fica como está: sem a busca do CEP, que trocava
  // rua e bairro pelos do CEP sem o lojista mexer, e com o bairro dele no seletor da taxa por bairro.
  const enderecoPronto = (a: { zipCode?: string | null; neighborhood?: string | null }) => {
    rawFeeInfo.marcarCepComoBuscado(a.zipCode || "");
    if (rawFeeInfo.feeType === "neighborhood") rawFeeInfo.setSelectedNeighborhood(a.neighborhood || "");
  };

  // O lojista mudou o lugar da entrega: a taxa gravada do pedido sai e vale a do endereço novo.
  const soltaTaxaDoPedido = (novo: Partial<DeliveryAddress>) => {
    if (taxaDoPedido !== null && chaveDoLugar(novo) !== taxaDoPedido) {
      setTaxaDoPedido(null);
      setFeeOverride(null);
    }
  };

  // Ajuste à mão (Editar, Restaurar) é decisão do lojista: a taxa gravada do pedido deixa de valer.
  const ajustarTaxa = (v: number | null) => {
    setTaxaDoPedido(null);
    setFeeOverride(v);
  };

  // Escolher endereço salvo preenche os campos e marca o id pra reusar no
  // servidor; qualquer edição manual posterior volta a ser "endereço novo".
  const pickSavedAddress = (addr: any) => {
    enderecoMexidoRef.current = true;
    enderecoPronto(addr);
    setSavedAddressId(addr.id);
    const endereco = {
      street: addr.street || "",
      number: addr.number || "",
      neighborhood: addr.neighborhood || "",
      complement: addr.complement || "",
      city: addr.city || "",
      state: addr.state || "",
      zipCode: addr.zipCode || "",
      reference: addr.reference || "",
      lat: addr.lat ?? null,
      lng: addr.lng ?? null,
      formattedAddress: "",
    } as DeliveryAddress;
    soltaTaxaDoPedido(endereco);
    setAddress(endereco);
  };

  const setAddressManual: typeof setAddress = (value) => {
    enderecoMexidoRef.current = true;
    setSavedAddressId(null);
    soltaTaxaDoPedido(typeof value === "function" ? value(address) : value);
    setAddress(value as any);
  };

  // Trocou o cliente: o endereço salvo escolhido era do outro (o servidor recusa endereço de outro cliente). Os campos
  // ficam, como endereço digitado.
  const escolherCliente = (next: CustomerSelection) => {
    const idDe = (c: CustomerSelection) => (c.kind === "existing" ? c.customer.id : null);
    if (idDe(next) !== idDe(customer)) setSavedAddressId(null);
    setCustomer(next);
  };

  // Carrega um pedido existente do PDV no carrinho pra edição (mesa/correção).
  const loadOrderForEdit = (order: any) => {
    setTaxaServicoPct(Number(order.taxaServicoPct) || 0);
    // Brinde de pontos não é item do carrinho: volta como prêmio escolhido (o servidor refaz a linha).
    const fotoPremios = Array.isArray(order.loyaltyRewards) ? (order.loyaltyRewards as Array<{ rewardId: string; type: string; points: number }>) : [];
    premiosDoClienteRef.current = order.customer?.id ?? null;
    setPremiosPdv(fotoPremios.map((r) => (r.type === "cashback" ? { rewardId: r.rewardId, points: r.points } : { rewardId: r.rewardId })));
    const loaded: CartItem[] = (order.items ?? []).filter((it: any) => !it.loyaltyRewardId).map((it: any) => {
      const found = (products as any[]).find((p) => p.id === it.productId);
      const price = parseFloat(it.price || "0") || 0;
      const product =
        found ??
        ({
          id: it.productId || genId(),
          name: it.productName || "Produto",
          price: String(price),
          stock: null,
          optionGroups: null,
          isFood: false,
          status: "ativo",
          // Produto apagado: a unidade do peso vem do item (ml continua ml na linha e na mensagem).
          ...(it.weightUnit ? { weightUnit: it.weightUnit } : {}),
        } as any);
      return {
        cartItemId: genId(),
        product,
        quantity: it.quantity || 1,
        notes: it.notes || "",
        selectedOptions: (it.selectedOptions ?? []) as SelectedOption[],
        removedIngredients: (it.removedIngredients ?? []) as string[],
        optionsTotal: parseFloat(it.optionsTotal || "0") || 0,
        unitPrice: price,
        originalUnitPrice:
          it.originalPrice != null ? parseFloat(it.originalPrice) || price : price,
        scheduledDate: it.scheduledDate || undefined,
        scheduledTime: it.scheduledTime || undefined,
        scheduledShift: it.scheduledShift || undefined,
        weightKg: it.weightKg != null ? parseFloat(it.weightKg) || undefined : undefined,
        ghost: !found,
      };
    });
    setCart(loaded);
    // Data única: o pedido aberto traz a data em comum dos itens; com datas diferentes (pedido de antes da opção)
    // cada item fica com a sua até o lojista escolher uma para todos.
    const comum = dataUnicaDosItens(loaded);
    setEncData(comum?.data ?? null);
    setEncHora(comum?.hora ?? null);
    setEncTurno((comum?.turno as Turno | null) ?? null);
    setOrderNotes(order.notes || "");
    // O pedido guarda o desconto já em valor absoluto — recarrega como R$ fixo.
    const disc = parseFloat(order.discountAmount || "0") || 0;
    setDiscountValue(disc);
    setDiscountType("fixed");
    const pending = order.paymentMethod === "Pendente";
    const isMixed =
      order.paymentMethod === "Misto" &&
      Array.isArray(order.paymentParts) &&
      order.paymentParts.length > 0;
    setPayLater(pending);
    setPaymentMixed(isMixed);
    setPaymentParts(isMixed ? order.paymentParts : []);
    setPaymentMethod(
      pending || isMixed ? acceptedPayments[0] || "" : order.paymentMethod || acceptedPayments[0] || "",
    );
    setCashReceived(order.cashReceived != null ? parseFloat(order.cashReceived) || 0 : 0);
    // Crediário: o nº de parcelas e a 1ª data são os gravados no pedido, não o padrão da forma nem a data em que o PDV
    // abriu (a edição gravava esses por cima das parcelas lançadas).
    const parcelasDoPedido = Math.min(Math.max(Math.trunc(Number(order.crediarioInstallments) || 0), 0), 24);
    const primeiraDoPedido = order.crediarioFirstDue ? String(order.crediarioFirstDue).slice(0, 10) : "";
    const formaCrediario = isMixed
      ? (order.paymentParts as PaymentPart[]).find((p) => crediarioNames.includes(p.method))?.method ?? null
      : crediarioNames.includes(order.paymentMethod) ? order.paymentMethod : null;
    formaDoPedidoRef.current = parcelasDoPedido > 0 ? formaCrediario : null;
    if (parcelasDoPedido > 0) setCrediarioInstallments(parcelasDoPedido);
    if (primeiraDoPedido) setCrediarioFirstDue(primeiraDoPedido);
    const parcelasLancadas = Number(order.crediarioParcelasLancadas) || 0;
    // «Cliente avulso» é o pedido sem cliente ou o balcão da loja (`__avulso__<storeId>`, sem sufixo). O cliente com nome
    // e sem número (`__avulso__<storeId>_<ts>`) abre com o nome e «Sem WhatsApp», como na lista de Clientes (Pedro,
    // 18/10/2026).
    const custWhats: string = order.customer?.whatsappNumber || "";
    const abriuAvulso = !order.customer || /^__avulso__[0-9a-f-]+$/i.test(custWhats);
    if (!abriuAvulso) {
      setCustomer({ kind: "existing", customer: order.customer });
    } else {
      setCustomer({ kind: "avulso" });
    }
    const isDelivery = order.deliveryMode === "entrega";
    setDeliveryMode(isDelivery ? "entrega" : order.deliveryMode === "combinar" ? "combinar" : "retirada");
    enderecoMexidoRef.current = false;
    if (isDelivery) {
      const a = order.address || {};
      enderecoPronto(a);
      const endereco = {
        street: a.street || "",
        number: a.number || "",
        neighborhood: a.neighborhood || "",
        complement: a.complement || "",
        city: a.city || "",
        state: a.state || "",
        zipCode: a.zipCode || "",
        reference: a.reference || "",
        lat: a.lat ?? null,
        lng: a.lng ?? null,
        formattedAddress: "",
      } as DeliveryAddress;
      setAddress(endereco);
      // Trava a taxa no valor do pedido enquanto o lugar da entrega não muda.
      setFeeOverride(parseFloat(order.deliveryFee || "0") || 0);
      setTaxaDoPedido(chaveDoLugar(endereco));
    } else {
      setAddress(EMPTY_ADDRESS);
      rawFeeInfo.setSelectedNeighborhood("");
      setFeeOverride(null);
      setTaxaDoPedido(null);
    }
    setSavedAddressId(null);
    setTableNumber(order.tableNumber || "");
    setEditingOrder({
      id: order.id,
      dailyOrderNumber: order.dailyOrderNumber ?? null,
      wasPending: pending,
      source: order.source === "link" ? "link" : "pdv",
      loyaltyPointsUsed: Number(order.loyaltyPointsUsed) || 0,
      deliveryFeePending: order.deliveryFeePending === true,
      diasAgendados: [...new Set(loaded.map((it) => it.scheduledDate).filter((d): d is string => !!d))],
      customerId: order.customerId ?? order.customer?.id ?? null,
      abriuAvulso,
      crediarioTravado: parcelasLancadas > 0 ? { parcelas: parcelasDoPedido || parcelasLancadas, primeira: primeiraDoPedido } : null,
      eraCombinar: order.deliveryMode === "combinar",
    });
    setSuccess(null);
    setNotifyPrompt(null);
    setShowCartMobile(true);
  };

  const cancelEdit = () => {
    handleClearCart();
  };

  const getCartItemQty = (productId: string) =>
    cart.filter((it) => it.product.id === productId).reduce((s, it) => s + it.quantity, 0);

  // kg já no carrinho para o produto (peso × embalagens, por linha).
  const getCartItemKg = (productId: string) =>
    cart
      .filter((it) => it.product.id === productId)
      .reduce((s, it) => s + (it.weightKg ?? 0) * it.quantity, 0);

  // Saldo em kg que ainda dá pra pesar (desconta o carrinho; na edição, a
  // própria linha editada não conta contra si mesma). null = sem controle.
  const remainingKg = (product: Product, excludeCartItemId?: string) => {
    const raw = (product as any).stockKg;
    if (raw == null || !(product as any).soldByWeight) return null;
    const total = parseFloat(String(raw));
    if (!Number.isFinite(total)) return null;
    const inCart = cart
      .filter((it) => it.product.id === product.id && it.cartItemId !== excludeCartItemId)
      .reduce((s, it) => s + (it.weightKg ?? 0) * it.quantity, 0);
    return Math.max(0, Math.round((total - inCart) * 1000) / 1000);
  };

  const stockReached = (product: Product) => {
    const p = product as any;
    if (p.soldByWeight) {
      if (p.stockKg == null) return false;
      const total = parseFloat(String(p.stockKg));
      return Number.isFinite(total) && getCartItemKg(product.id) >= total;
    }
    const stock = p.stock;
    if (stock === null || stock === undefined) return false;
    return getCartItemQty(product.id) >= stock;
  };

  const handleQuickAdd = (product: Product) => {
    if (productHasOptions(product) || ((product as any).isOrderBased && !dataUnica) || (product as any).soldByWeight) {
      setEditingItem(null);
      setDetailProduct(product);
      return;
    }
    if (stockReached(product)) return;
    setCart((prev) => [...prev, buildSimpleCartItem(product, 1)]);
  };

  const handleQuickRemove = (product: Product) => {
    const items = cart.filter((it) => it.product.id === product.id);
    if (items.length === 0) return;
    const last = items[items.length - 1];
    handleQtyChange(last.cartItemId, last.quantity - 1);
  };

  const handleQuickIncrement = (product: Product) => {
    if (productHasOptions(product) || ((product as any).isOrderBased && !dataUnica) || (product as any).soldByWeight) {
      setEditingItem(null);
      setDetailProduct(product);
      return;
    }
    if (stockReached(product)) return;
    const items = cart.filter((it) => it.product.id === product.id);
    if (items.length === 0) {
      setCart((prev) => [...prev, buildSimpleCartItem(product, 1)]);
      return;
    }
    const last = items[items.length - 1];
    handleQtyChange(last.cartItemId, last.quantity + 1);
  };

  async function printPDVOrder(payload: any) {
    if (!printer.connected) return;
    const storeName = (payload.store as any)?.name || "Minha Loja";
    const order = payload.order;
    if (!order) return;
    const fullOrder = buildFullOrderForReceipt(payload);
    const receipt = orderToReceipt(fullOrder, {
      hideDailyNumber: (payload.store as any)?.dailyOrderNumbering === false,
      timezone: ((payload.store as any)?.timezone as string | undefined) ?? null,
      hideGroupTitles: (payload.store as any)?.showGroupTitles === false,
      orderCodeStyle: (payload.store as any)?.orderCodeStyle ?? null,
      topMargin: (payload.store as any)?.receiptTopMargin ?? null,
      showPaidCheckbox: (payload.store as any)?.receiptPaidCheckbox === true,
      receiptFont: (payload.store as any)?.pdvSettings?.receiptFont ?? null,
      // O rodapé que sai agora (null sem o plano): /api/stores/me no painel, /api/pdv/:slug no Link do PDV.
      footerMessage: (payload.store as any)?.receiptFooterPrinted ?? null,
      porCliente: (payload.store as any)?.tableQrPerClient === true,
    });
    const data = formatReceiptBytes(storeName, receipt, printer.prefs.paperWidth, printer.isAsciiOnlyPrinter);
    await printer.printData(data, order.id, "pdv");
  }

  // `entregar` («Pago e marcar como entregue», 19/10/2026): fecha a conta como o «Só registrar pagamento» e marca o
  // pedido Entregue. Conta paga não sai de Comandas abertas enquanto não for Entregue, e o PDV não tinha como fazer isso.
  const finalizar = async (opcoes: { entregar?: boolean } = {}) => {
    if (cart.length === 0) {
      toast({
        title: "Carrinho vazio",
        description: "Adicione produtos antes de finalizar.",
        variant: "destructive",
      });
      return;
    }
    if (customerRequired && customer.kind === "avulso") {
      toast({
        title: "Cliente obrigatório",
        description: "Identifique o cliente antes de finalizar.",
        variant: "destructive",
      });
      return;
    }
    if (
      dataUnica &&
      !encomendaCompleta &&
      cart.some((it) => (it.product as any)?.isOrderBased && !it.scheduledDate)
    ) {
      const oQue = modoEnc === "turno" ? "a data e o turno" : modoEnc === "data" ? "a data" : "a data e o horário";
      toast({
        title: `Escolha ${oQue} da encomenda`,
        description: "Fica no carrinho, junto da encomenda.",
        variant: "destructive",
      });
      return;
    }
    if (
      deliveryMode === "entrega" &&
      (!address.street.trim() || !address.number.trim() || !address.neighborhood.trim())
    ) {
      toast({
        title: "Endereço incompleto",
        description: "Informe rua, número e bairro.",
        variant: "destructive",
      });
      return;
    }
    if (deliveryMode === "entrega" && feeInfo.noDeliveryBlocked) {
      toast({
        title: "Não entregamos nesse endereço",
        description: "Selecione retirada no local ou outro endereço.",
        variant: "destructive",
      });
      return;
    }
    if (deliveryMode === "entrega" && feeInfo.feeNotResolved) {
      toast({ title: "Aguarde o cálculo da taxa de entrega", variant: "destructive" });
      return;
    }
    if (minOrderValue > 0 && subtotal < minOrderValue) {
      toast({
        title: "Pedido mínimo não atingido",
        description: `Valor mínimo: ${formatCurrency(minOrderValue)}`,
        variant: "destructive",
      });
      return;
    }
    if (!payLater) {
      if (paymentMixed) {
        if (paymentParts.length === 0) {
          toast({
            title: "Pagamento misto",
            description: "Adicione pelo menos uma forma de pagamento.",
            variant: "destructive",
          });
          return;
        }
        const diff = Math.abs(paymentPartsTotal - total);
        if (diff > 0.01) {
          toast({
            title: "Pagamento misto",
            description: `A soma das partes (${formatCurrency(paymentPartsTotal)}) não bate com o total (${formatCurrency(total)}).`,
            variant: "destructive",
          });
          return;
        }
        const cashPart = paymentParts.find((p) => p.method === "Dinheiro");
        if (cashPart && cashReceived > 0 && cashReceived + 0.01 < cashPart.amount) {
          toast({
            title: "Valor recebido insuficiente",
            description: `O valor recebido (${formatCurrency(cashReceived)}) é menor que a parte em dinheiro (${formatCurrency(cashPart.amount)}).`,
            variant: "destructive",
          });
          return;
        }
      } else if (paymentMethod === "Dinheiro" && cashReceived > 0 && cashReceived + 0.01 < total) {
        toast({
          title: "Valor recebido insuficiente",
          description: `O valor recebido (${formatCurrency(cashReceived)}) é menor que o total do pedido (${formatCurrency(total)}).`,
          variant: "destructive",
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      // Na edição o cliente só vai quando o lojista trocou: ausente mantém o do pedido.
      let customerPayload: any = undefined;
      if (customer.kind === "existing") {
        if (!editingOrder) {
          customerPayload = {
            id: customer.customer.id,
            name: customer.customer.firstName,
            whatsapp: customer.customer.whatsappNumber,
          };
        } else if (customer.customer.id !== editingOrder.customerId) {
          customerPayload = { id: customer.customer.id };
        }
      } else if (customer.kind === "avulso") {
        // Tirou o cliente do pedido: null = Cliente avulso. O que abriu como avulso (o balcão) e ficou assim não vai.
        if (editingOrder && !editingOrder.abriuAvulso) customerPayload = null;
      } else if (customer.kind === "new") {
        const cleanWhats = customer.whatsapp.replace(/\D/g, "");
        if (!customer.name.trim()) {
          toast({
            title: "Nome obrigatório",
            description: "Informe o nome do novo cliente.",
            variant: "destructive",
          });
          setSubmitting(false);
          return;
        }
        if (cleanWhats && cleanWhats.length < 10) {
          toast({
            title: "WhatsApp inválido",
            description: "Informe um número válido ou deixe em branco.",
            variant: "destructive",
          });
          setSubmitting(false);
          return;
        }
        customerPayload = {
          name: customer.name.trim(),
          whatsapp: cleanWhats || "",
          ddi: customer.ddi || "55",
        };
      }

      const items = cart.map((it) => ({
        productId: it.product.id,
        quantity: it.quantity,
        notes: it.notes || undefined,
        selectedOptions: it.selectedOptions.length > 0 ? it.selectedOptions : undefined,
        removedIngredients: it.removedIngredients.length > 0 ? it.removedIngredients : undefined,
        scheduledDate: dataUnica && (it.product as any)?.isOrderBased && encomendaCompleta ? encData! : it.scheduledDate,
        scheduledTime: dataUnica && (it.product as any)?.isOrderBased && encomendaCompleta ? (modoEnc === "horario" ? encHora ?? undefined : undefined) : it.scheduledTime,
        scheduledShift: dataUnica && (it.product as any)?.isOrderBased && encomendaCompleta ? (modoEnc === "turno" ? encTurno ?? undefined : undefined) : it.scheduledShift,
        appliedUnitPrice: it.unitPrice,
        weightKg: it.weightKg ?? undefined,
      }));

      const effectivePayLater = payLater && !!tableNumber.trim();
      const body: any = {
        items,
        deliveryMode,
        paymentMethod: effectivePayLater
          ? "Pendente"
          : paymentMixed
            ? "Misto"
            : paymentMethod,
        deliveryFee: deliveryMode === "entrega" ? deliveryFee : 0,
        notes: orderNotes || undefined,
        source,
        customer: customerPayload,
      };
      if (tableNumber.trim()) body.tableNumber = tableNumber.trim();
      const crediarioSelected =
        !effectivePayLater &&
        (paymentMixed
          ? paymentParts.some((p) => crediarioNames.includes(p.method))
          : crediarioNames.includes(paymentMethod));
      if (crediarioSelected) {
        body.crediarioInstallments = parcelas;
        // Travado sem a 1ª data gravada (pedido antigo): vai null, que é o que o pedido tem (texto vazio dá 400).
        body.crediarioFirstDue = parcelasTravadas && !primeiraParcela ? null : primeiraParcela;
      }
      if (deliveryMode === "entrega") {
        if (savedAddressId) body.addressId = savedAddressId;
        body.address = {
          street: address.street,
          number: address.number,
          neighborhood: address.neighborhood,
          complement: address.complement,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
          reference: address.reference,
          ...(address.lat != null ? { lat: address.lat } : {}),
          ...(address.lng != null ? { lng: address.lng } : {}),
        };
      }
      if (discountAmount > 0) {
        body.discountAmount = discountValue;
        body.discountType = discountType;
      }
      // Prêmios de pontos: na edição a lista vai sempre (vazia = tirar os prêmios); no pedido novo, só com prêmio.
      if (pontosNoPdv && (editingOrder || rewardUsesPdv.length > 0)) {
        body.loyaltyRewards = rewardUsesPdv.map((u) => ({
          rewardId: u.rewardId,
          ...(u.type === "cashback" ? { points: u.points } : {}),
        }));
      }
      if (!effectivePayLater) {
        if (paymentMixed && paymentParts.length > 0) body.paymentParts = paymentParts;
        if (!paymentMixed && paymentMethod === "Dinheiro" && cashReceived > 0)
          body.cashReceived = cashReceived;
        if (
          paymentMixed &&
          paymentParts.some((p) => p.method === "Dinheiro") &&
          cashReceived > 0
        )
          body.cashReceived = cashReceived;
        if (surcharge > 0) body.cardSurcharge = surcharge;
      }

      const isEdit = !!editingOrder;
      // Avisa se ESTE aparelho vai imprimir a venda (mesma condição da impressão
      // automática lá embaixo). Aí o servidor não manda pro app e a estação ?print
      // não repete: cada venda de PDV sai uma vez (decidirImpressaoPdv no servidor).
      if (isEdit) {
        // O servidor só troca o endereço do pedido quando o lojista mexeu nele (aba sem o campo: edita como antes).
        body.addressChanged = enderecoMexidoRef.current;
      } else {
        body.localPrint = !!(pdvSettings.autoPrint && printer.connected);
      }
      // Frete a combinar (acima da última faixa, ou bairro fora da lista com «Calcular após o pedido»): o pedido sai
      // com o frete pendente ("Enviar frete"). Na edição também, como a retirada que vira entrega acima da última faixa.
      if (deliveryMode === "entrega" && feeInfo.feePending && deliveryFee === 0) body.deliveryFeePending = true;
      const result = await customFetch<any>(
        isEdit ? `${ordersEndpoint}/${editingOrder!.id}/pdv` : ordersEndpoint,
        {
          method: isEdit ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );

      const orderNumber = orderDisplayCode(result, (store as any)?.orderCodeStyle);
      const closedAccount = isEdit && editingOrder!.wasPending && !effectivePayLater;
      // O mesmo PUT do «Alterar sem Enviar» do Pedidos: receita, pontos, custo e histórico, sem mensagem ao cliente.
      // Falhou: o pagamento já ficou, e a conta segue em Comandas abertas com «pago» (lá tem «Marcar como entregue»).
      let entregou = false;
      if (closedAccount && opcoes.entregar) {
        try {
          await customFetch(`${ordersEndpoint}/${result.id}/status`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ status: "entregue", notifyCustomer: false }),
          });
          entregou = true;
        } catch (err) {
          toast({
            title: "Pagamento registrado, mas o pedido não foi marcado como entregue",
            description: `${extractApiErrorMessage(err, "Tente de novo.")} Use «Marcar como entregue» em Comandas abertas.`,
            variant: "destructive",
          });
        }
      }

      // Pedido do link alterado: monta o aviso e deixa o lojista decidir.
      // Respeita o toggle "Sem popup" do template em Configurações.
      if (isEdit && editingOrder!.source === "link") {
        const tpl = (store as any)?.statusMessageTemplates?.pedido_alterado as
          | { template: string; enabled: boolean }
          | undefined;
        // Cliente sem WhatsApp de verdade: o aviso abre o WhatsApp sem destinatário (💡 #87).
        const phone = customer.kind === "existing" ? telefoneDoLink(customer.customer.whatsappNumber) : "";
        if ((tpl ? tpl.enabled : true) && customer.kind === "existing") {
          const itemLines = cart.map((it) => {
            const lt = (it.unitPrice + it.optionsTotal) * it.quantity;
            const extras: string[] = [];
            if (it.selectedOptions.length > 0) extras.push(it.selectedOptions.map((o) => o.optionName).join(", "));
            if (it.removedIngredients?.length) extras.push(`Sem: ${it.removedIngredients.join(", ")}`);
            if (it.notes) extras.push(`Obs: ${it.notes}`);
            return `• ${it.quantity}x ${(it.product as any).name} - ${formatCurrency(lt)}${extras.length ? ` (${extras.join("; ")})` : ""}`;
          });
          const clientName =
            customer.kind === "existing" ? customer.customer.firstName || "Cliente" : "Cliente";
          const showNumber = (store as any)?.dailyOrderNumbering !== false;
          let msg = (tpl?.template || EDITED_ORDER_MESSAGE_DEFAULT)
            .replace(/\{cliente_nome\}/g, clientName)
            .replace(/\{itens\}/g, itemLines.join("\n"))
            .replace(/\{total\}/g, formatCurrency(total))
            .replace(/\{loja_nome\}/g, (store as any)?.name || "");
          msg = showNumber
            ? msg.replace(/\{numero_pedido\}/g, orderNumber)
            : msg.replace(/\s*#?\{numero_pedido\}/g, "");
          setNotifyPrompt({ message: msg, phone });
        }
      }
      const printable = {
        order: result,
        items: cart,
        store,
        subtotal,
        discountAmount,
        loyaltyDiscount: rewardMathPdv.loyaltyDiscount,
        loyaltyRewards: rewardUsesPdv,
        surcharge,
        total,
        cashReceived,
        cashChange,
        paymentParts,
        paymentMixed,
        paymentMethod,
        payLater: effectivePayLater,
        tableNumber: tableNumber.trim() || null,
        customer,
        deliveryMode,
        deliveryFee: deliveryMode === "entrega" ? deliveryFee : 0,
        // Frete pendente: o cupom deste aparelho e o comprovante dizem «a calcular» (antes saía «Gratis»).
        deliveryFeePending:
          deliveryMode === "entrega" && deliveryFee === 0 && (body.deliveryFeePending === true || (isEdit && editingOrder!.deliveryFeePending)),
        address: deliveryMode === "entrega" ? address : null,
        orderNotes,
        orderNumber,
      };

      setSuccess({
        orderId: result.id,
        orderNumber,
        title: closedAccount
          ? "Conta fechada!"
          : isEdit
            ? "Pedido atualizado!"
            : "Pedido registrado!",
        message: closedAccount
          ? entregou
            ? "Pagamento registrado e pedido marcado como entregue."
            : "Pagamento registrado e a receita entrou no Financeiro."
          : isEdit
            ? "As alterações foram salvas — o Financeiro acompanhou o novo total."
            : "Pedido registrado com sucesso!",
        printablePayload: printable,
      });

      if (pdvSettings.autoPrint && printer.connected) {
        try {
          await printPDVOrder(printable);
        } catch (err) {
          console.error("Auto-print failed", err);
        }
      }
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, "Falha ao registrar pedido.");
      toast({ title: "Erro ao finalizar", description: msg, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };
  // Sem argumento: o botão passa o evento do clique, que não pode virar opção.
  const handleFinalize = () => finalizar();
  const handleCloseAndDeliver = () => finalizar({ entregar: true });

  const handleNewSale = () => {
    handleClearCart();
    setSuccess(null);
    setShowCartMobile(false);
  };

  const handlePrint = async () => {
    if (!success) return;
    if (!printer.connected) {
      toast({
        title: "Impressora não conectada",
        description: "Conecte a impressora térmica nas Configurações.",
        variant: "destructive",
      });
      return;
    }
    try {
      await printPDVOrder(success.printablePayload);
      toast({ title: "Imprimindo!" });
    } catch (err: any) {
      toast({
        title: "Erro ao imprimir",
        description: err?.message || "Falha",
        variant: "destructive",
      });
    }
  };

  // Aviso de pedido alterado: abre o WhatsApp com o texto (o envio é do
  // lojista, dentro do WhatsApp) e fecha o prompt.
  const sendNotify = () => {
    if (!notifyPrompt) return;
    abreWhatsApp(linkDoWhatsApp(notifyPrompt.phone, notifyPrompt.message));
    setNotifyPrompt(null);
  };
  const dismissNotify = () => setNotifyPrompt(null);

  // Comprovante: sem número de verdade (balcão, cliente salvo sem número, cliente novo sem número) abre o WhatsApp sem
  // destinatário, com a mensagem pronta, para a lojista escolher o contato (💡 #87, Pedro: «Abra sem destinatário»).
  const handleSendWhatsApp = () => {
    if (!success) return;
    let phoneFull = "";
    if (customer.kind === "existing") phoneFull = telefoneDoLink(customer.customer.whatsappNumber);
    if (customer.kind === "new") {
      const target = customer.whatsapp.replace(/\D/g, "");
      const ddi = (customer.ddi || "55").replace(/\D/g, "");
      if (target.length >= 10) phoneFull = target.length > 11 ? target : `${ddi}${target}`;
    }
    abreWhatsApp(linkDoWhatsApp(phoneFull, buildWhatsAppMessage(success.printablePayload)));
  };

  return {
    // raw printer/toast handles
    printer,

    // store-derived config
    acceptedPayments,
    crediarioNames,
    crediarioInterest,
    crediarioInstallments: parcelas,
    setCrediarioInstallments,
    crediarioFirstDue: primeiraParcela,
    setCrediarioFirstDue,
    crediarioTravado: !!parcelasTravadas,
    allowsDelivery,
    customerRequired,
    minOrderValue,
    pdvSettings,
    dataUnica,
    encData,
    encHora,
    encTurno,
    horariosDosTurnos,
    setEncomenda: (d: string | null, h: string | null, t: Turno | null = null) => {
      setEncData(d);
      setEncHora(h);
      setEncTurno(t);
    },

    // search/filter
    search,
    setSearch,
    activeCategory,
    setActiveCategory,
    filteredProducts,

    // detail/editing
    detailProduct,
    setDetailProduct,
    editingItem,
    setEditingItem,

    // cart + state
    cart,
    orderNotes,
    setOrderNotes,
    discountValue,
    setDiscountValue,
    discountType,
    setDiscountType,
    customer,
    setCustomer: escolherCliente,
    deliveryMode,
    setDeliveryMode,
    address,
    setAddress,
    paymentMixed,
    setPaymentMixed,
    paymentMethod,
    setPaymentMethod,
    cashReceived,
    setCashReceived,
    paymentParts,
    setPaymentParts,
    submitting,
    success,
    setSuccess,
    showCartMobile,
    setShowCartMobile,
    feeOverride,
    setFeeOverride: ajustarTaxa,
    useCustomDelivery,

    // mesa/comanda + edição de pedido
    tableNumber,
    setTableNumber,
    payLater,
    setPayLater,
    editingOrder,
    canalDaPromo,
    loadOrderForEdit,
    cancelEdit,
    notifyPrompt,
    setNotifyPrompt,
    sendNotify,
    dismissNotify,

    // endereços salvos (PDV interno)
    savedAddressId,
    pickSavedAddress,
    setAddressManual,

    // programa de pontos (PDV do painel)
    pontosNoPdv,
    programaPdv,
    saldoPdv,
    premiosPdv,
    setPremiosPdv: escolherPremiosPdv,
    problemaPdv,
    rewardUsesPdv,
    rewardMathPdv,
    rendimentoPdv,

    // derived
    subtotal,
    discountAmount,
    baseAfterDiscount,
    feeInfo,
    deliveryFee,
    freeDeliveryApplied,
    surcharge,
    taxaServico,
    taxaServicoPct,
    total,
    cashChange,
    paymentPartsTotal,

    // handlers
    handleAddOrUpdateItem,
    handleRemoveItem,
    handleQtyChange,
    handleClearCart,
    getCartItemQty,
    stockReached,
    remainingKg,
    handleQuickAdd,
    handleQuickRemove,
    handleQuickIncrement,
    handleFinalize,
    handleCloseAndDeliver,
    handleNewSale,
    handlePrint,
    handleSendWhatsApp,
  };
}

export type UsePdvCartReturn = ReturnType<typeof usePdvCart>;
